/**
 * Column Mapper Component
 *
 * Maps CSV/Excel columns to application fields.
 * Groups the destination dropdown into Standard Fields and Custom Fields,
 * and offers an inline "Create new field" flow for columns that don't match
 * any existing field.
 */

import { useState, useMemo, useCallback } from 'react';
import { ArrowRight, Check, X, AlertCircle, Plus, Loader2 } from 'lucide-react';
import { suggestColumnMappings } from '../../../lib/excelService';

// ============================================================================
// Types
// ============================================================================

export interface AppField {
  key: string;
  label: string;
  required: boolean;
  type?: 'string' | 'number' | 'date' | 'boolean';
  /** When true the value will be routed into items.metadata by the modal */
  isCustomField?: boolean;
  /** Optional hint shown in the column mapper UI */
  description?: string;
}

export interface ColumnMappingState {
  [excelColumn: string]: string | null; // null = unmapped / skipped
}

export interface ColumnMapperProps {
  excelHeaders: string[];
  /** Standard + already-existing custom fields */
  appFields: AppField[];
  initialMappings?: ColumnMappingState;
  onChange: (mappings: ColumnMappingState) => void;
  /**
   * Called when the user clicks "Create Field" in the inline creation panel.
   * The parent is responsible for persisting the field and returning an AppField
   * with the final key. The mapper auto-maps the column on success.
   */
  onCreateField?: (
    forColumn: string,
    draft: { label: string; type: NonNullable<AppField['type']> }
  ) => Promise<AppField>;
}

// Sentinel value used in the <select> to open the inline creation panel
const CREATE_SENTINEL = '__create_new__';

// ============================================================================
// Component
// ============================================================================

export function ColumnMapper({
  excelHeaders,
  appFields,
  initialMappings,
  onChange,
  onCreateField,
}: ColumnMapperProps) {
  // Auto-suggest on first render
  const [mappings, setMappings] = useState<ColumnMappingState>(() => {
    if (initialMappings) return initialMappings;

    const suggestions = suggestColumnMappings(
      excelHeaders,
      appFields.map(f => ({ key: f.key, label: f.label }))
    );

    return Object.fromEntries(excelHeaders.map(h => [h, suggestions[h] || null]));
  });

  // Per-column "create new field" state
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [draftLabel, setDraftLabel] = useState('');
  const [draftType, setDraftType] = useState<NonNullable<AppField['type']>>('string');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Partition fields
  const standardFields = useMemo(() => appFields.filter(f => !f.isCustomField), [appFields]);
  const customFields   = useMemo(() => appFields.filter(f => f.isCustomField), [appFields]);

  // Required fields not yet mapped
  const missingRequired = useMemo(() => {
    const mapped = new Set(Object.values(mappings).filter(Boolean));
    return appFields.filter(f => f.required && !mapped.has(f.key)).map(f => f.label);
  }, [mappings, appFields]);

  // Fields available to map to (not yet chosen by any column)
  const usedFieldKeys = useMemo(
    () => new Set(Object.values(mappings).filter(Boolean) as string[]),
    [mappings]
  );

  const updateMapping = useCallback(
    (col: string, fieldKey: string | null) => {
      const next = { ...mappings, [col]: fieldKey };
      setMappings(next);
      onChange(next);
    },
    [mappings, onChange]
  );

  const handleSelectChange = useCallback(
    (col: string, value: string) => {
      if (value === CREATE_SENTINEL) {
        setCreatingFor(col);
        setDraftLabel(col); // Pre-fill with column header
        setDraftType('string');
        setCreateError(null);
      } else {
        if (creatingFor === col) setCreatingFor(null);
        updateMapping(col, value || null);
      }
    },
    [creatingFor, updateMapping]
  );

  const handleCreateField = useCallback(async () => {
    if (!creatingFor || !onCreateField) return;

    const label = draftLabel.trim();
    if (!label) { setCreateError('Field name is required'); return; }

    setIsCreating(true);
    setCreateError(null);
    try {
      const newField = await onCreateField(creatingFor, { label, type: draftType });
      updateMapping(creatingFor, newField.key);
      setCreatingFor(null);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create field');
    } finally {
      setIsCreating(false);
    }
  }, [creatingFor, draftLabel, draftType, onCreateField, updateMapping]);

  const handleAutoMap = useCallback(() => {
    const suggestions = suggestColumnMappings(
      excelHeaders,
      appFields.map(f => ({ key: f.key, label: f.label }))
    );
    const next = Object.fromEntries(excelHeaders.map(h => [h, suggestions[h] || null]));
    setMappings(next);
    onChange(next);
  }, [excelHeaders, appFields, onChange]);

  const handleClearAll = useCallback(() => {
    const next = Object.fromEntries(excelHeaders.map(h => [h, null]));
    setMappings(next);
    onChange(next);
    setCreatingFor(null);
  }, [excelHeaders, onChange]);

  const mappedCount = Object.values(mappings).filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-[var(--text-pri)]">Map Columns to Fields</h3>
        <div className="flex items-center gap-2 text-sm">
          <button type="button" onClick={handleAutoMap} className="text-primary-600 hover:text-primary-700">
            Auto-detect
          </button>
          <span className="text-[var(--text-ter)]">|</span>
          <button type="button" onClick={handleClearAll} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            Clear all
          </button>
        </div>
      </div>

      {/* Missing required fields warning */}
      {missingRequired.length > 0 && (
        <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-[var(--mustard)]">Missing required fields</p>
            <p className="text-sm text-[var(--mustard)] mt-0.5">{missingRequired.join(', ')}</p>
          </div>
        </div>
      )}

      {/* Mapping rows */}
      <div className="border border-[var(--rule)] overflow-hidden">
        {/* Header row */}
        <div className="grid grid-cols-[1fr,auto,1fr] gap-2 px-3 py-2 bg-[var(--surface)] border-b border-[var(--rule)] text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">
          <div>CSV Column</div>
          <div />
          <div>App Field</div>
        </div>

        <div className="divide-y divide-[var(--rule)]">
          {excelHeaders.map(header => {
            const mapped = mappings[header];
            const isCreatingHere = creatingFor === header;

            return (
              <div key={header}>
                {/* Main row */}
                <div className="grid grid-cols-[1fr,auto,1fr] gap-2 px-3 py-2.5 items-center">
                  {/* Column name */}
                  <div className="text-sm font-medium text-[var(--text-pri)] truncate" title={header}>
                    {header}
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center justify-center w-8">
                    <ArrowRight className={`h-4 w-4 ${mapped ? 'text-[var(--green)]' : 'text-[var(--text-ter)]'}`} />
                  </div>

                  {/* Field selector */}
                  <select
                    value={isCreatingHere ? CREATE_SENTINEL : (mapped ?? '')}
                    onChange={e => handleSelectChange(header, e.target.value)}
                    className={`w-full px-2 py-1.5 text-sm border  focus:outline-none focus:ring-1 focus:ring-primary-400 ${
                      isCreatingHere
                        ? 'border-primary-300 bg-primary-50 text-primary-800'
                        : mapped
                        ? 'border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                        : 'border-[var(--rule)] text-[var(--text-sec)]'
                    }`}
                  >
                    <option value="">— Skip this column —</option>

                    {/* Standard fields */}
                    {standardFields.length > 0 && (
                      <optgroup label="Standard Fields">
                        {standardFields.map(f => (
                          <option
                            key={f.key}
                            value={f.key}
                            disabled={usedFieldKeys.has(f.key) && mapped !== f.key}
                          >
                            {f.label}
                            {f.required ? ' *' : ''}
                            {usedFieldKeys.has(f.key) && mapped !== f.key ? ' (in use)' : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}

                    {/* Custom fields */}
                    {customFields.length > 0 && (
                      <optgroup label="Custom Fields">
                        {customFields.map(f => (
                          <option
                            key={f.key}
                            value={f.key}
                            disabled={usedFieldKeys.has(f.key) && mapped !== f.key}
                          >
                            {f.label}
                            {usedFieldKeys.has(f.key) && mapped !== f.key ? ' (in use)' : ''}
                          </option>
                        ))}
                      </optgroup>
                    )}

                    {/* Create new field option */}
                    {onCreateField && (
                      <optgroup label="">
                        <option value={CREATE_SENTINEL}>＋ Create new field…</option>
                      </optgroup>
                    )}
                  </select>
                </div>

                {/* Inline create panel */}
                {isCreatingHere && onCreateField && (
                  <div className="mx-3 mb-3 p-3 bg-primary-50 border border-primary-200 space-y-3">
                    <p className="text-xs font-medium text-primary-800 uppercase tracking-wide">
                      New Custom Field
                    </p>

                    <div className="grid grid-cols-[1fr,auto] gap-2">
                      <div className="space-y-1">
                        <label className="text-xs text-[var(--text-sec)]">Field name</label>
                        <input
                          type="text"
                          value={draftLabel}
                          onChange={e => setDraftLabel(e.target.value)}
                          placeholder="e.g. Business Impact"
                          className="w-full px-2 py-1.5 text-sm border border-[var(--rule)] focus:outline-none focus:ring-1 focus:ring-primary-400"
                          onKeyDown={e => e.key === 'Enter' && handleCreateField()}
                          autoFocus
                        />
                      </div>

                      <div className="space-y-1">
                        <label className="text-xs text-[var(--text-sec)]">Type</label>
                        <select
                          value={draftType}
                          onChange={e => setDraftType(e.target.value as NonNullable<AppField['type']>)}
                          className="px-2 py-1.5 text-sm border border-[var(--rule)] focus:outline-none focus:ring-1 focus:ring-primary-400"
                        >
                          <option value="string">Text</option>
                          <option value="number">Number</option>
                          <option value="boolean">Yes / No</option>
                          <option value="date">Date</option>
                        </select>
                      </div>
                    </div>

                    {createError && (
                      <p className="text-xs text-[var(--coral)] flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {createError}
                      </p>
                    )}

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handleCreateField}
                        disabled={isCreating}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60"
                      >
                        {isCreating ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Plus className="h-3.5 w-3.5" />
                        )}
                        {isCreating ? 'Creating…' : 'Create Field'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setCreatingFor(null); updateMapping(header, null); }}
                        className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between text-sm text-[var(--text-sec)]">
        <span>{mappedCount} of {excelHeaders.length} columns mapped</span>
        <span className={missingRequired.length === 0 ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--mustard)]'}>
          {missingRequired.length === 0 ? (
            <span className="flex items-center gap-1">
              <Check className="h-4 w-4" />
              All required fields mapped
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <X className="h-4 w-4" />
              {missingRequired.length} required field{missingRequired.length !== 1 ? 's' : ''} missing
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

export default ColumnMapper;
